import { Link } from "react-router-dom";
import { Seo } from "../components/Seo";
import { Button } from "../components/ui/button";
import { Plane, Printer, ArrowRight, CheckSquare } from "lucide-react";

const SECTIONS = [
  {
    title: "1 · Regulations (Part 107)",
    items: [
      "Remote Pilot Certificate eligibility & registration (must be 16+)",
      "Aircraft registration and marking requirements",
      "Operating rules: 400 ft AGL limit, daylight/civil twilight, VLOS",
      "Waivable vs. non-waivable operating limitations",
      "Right-of-way, yielding to manned aircraft, no careless/reckless ops",
      "Reporting accidents to the FAA (serious injury / $500+ property damage)",
    ],
  },
  {
    title: "2 · Airspace & Charts",
    items: [
      "Read sectional charts: Class B, C, D, E and G airspace",
      "Identify controlled vs. uncontrolled airspace and floors/ceilings",
      "LAANC authorizations and how to request airspace access",
      "Special use airspace: MOAs, restricted, prohibited, TFRs",
      "Latitude/longitude and chart symbology basics",
    ],
  },
  {
    title: "3 · Weather",
    items: [
      "Decode METARs and TAFs",
      "Density altitude and its effect on performance",
      "Stability, temperature/dew point, fog and cloud formation",
      "Wind, gusts and micro-scale weather hazards",
      "Go / no-go decision making",
    ],
  },
  {
    title: "4 · Loading & Performance",
    items: [
      "Weight & balance and center of gravity effects",
      "Payload effects on endurance and control",
      "Battery care, temperature and safe charging",
    ],
  },
  {
    title: "5 · Operations",
    items: [
      "Pre-flight inspection checklist and crew resource management",
      "Physiological factors: vision, dehydration, drugs & alcohol (8-hr rule)",
      "Maintenance and inspection recordkeeping",
      "Radio communication phraseology basics",
      "Emergency procedures & lost-link behavior",
    ],
  },
  {
    title: "6 · Exam Logistics",
    items: [
      "60 multiple-choice questions · 70% (42 correct) to pass",
      "Schedule at an FAA-approved Knowledge Testing Center",
      "Bring government-issued photo ID",
      "Take unlimited practice quizzes until you consistently hit 85%+",
    ],
  },
];

export default function Checklist() {
  return (
    <div className="min-h-screen">
      <Seo
        title="Free FAA Part 107 Study Checklist | DronAcademy"
        description="A free, printable FAA Part 107 study checklist covering regulations, airspace, weather, loading and operations. Track your exam readiness with DronAcademy."
        path="/part-107-checklist"
      />

      <header className="glass border-b border-border/60 sticky top-0 z-50 print:hidden">
        <div className="max-w-4xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="checklist-logo">
            <div className="h-9 w-9 rounded-sm bg-primary flex items-center justify-center glow-cyan">
              <Plane className="h-5 w-5 text-primary-foreground" strokeWidth={2.5} />
            </div>
            <span className="font-display font-black text-lg">DRON<span className="text-primary">ACADEMY</span></span>
          </Link>
          <Button variant="outline" className="rounded-sm border-border" onClick={() => window.print()} data-testid="checklist-print">
            <Printer className="h-4 w-4 mr-2" /> Print / Save PDF
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12">
        <span className="inline-block text-xs font-mono uppercase tracking-[0.3em] text-primary mb-4">Free Resource</span>
        <h1 className="font-display text-4xl sm:text-5xl font-black leading-[1.05] mb-4">FAA Part 107 Study Checklist</h1>
        <p className="text-base md:text-lg text-slate-300 mb-10 max-w-2xl leading-relaxed">
          Everything you need to master before sitting the FAA Part 107 Remote Pilot knowledge test. Work top to bottom, and check each box as you go.
        </p>

        <div className="space-y-8" data-testid="checklist-sections">
          {SECTIONS.map((s) => (
            <section key={s.title} className="p-6 rounded-sm border border-border/70 bg-card break-inside-avoid">
              <h2 className="font-display text-xl font-bold mb-4 text-primary">{s.title}</h2>
              <ul className="space-y-3">
                {s.items.map((item) => (
                  <li key={item} className="flex items-start gap-3 text-slate-200">
                    <CheckSquare className="h-5 w-5 text-primary/70 mt-0.5 shrink-0" />
                    <span className="text-[15px] leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>

        <div className="mt-12 p-8 rounded-sm border border-primary/40 bg-primary/5 text-center print:hidden">
          <h2 className="font-display text-2xl font-bold mb-2">Want the full course + practice quizzes?</h2>
          <p className="text-slate-400 mb-6">Create your free account and start the Basics module today — no payment needed.</p>
          <Link to="/register">
            <Button size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm h-12 px-8 font-semibold" data-testid="checklist-cta">
              Create your free account <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </main>
    </div>
  );
}
